// src/pages/DashboardPage.tsx

import { useState } from 'react';
import { Dashboard } from '../components/Dashboard';
import { ConversationList } from '../components/ConversationList';
import { MessageThread } from '../components/MessageThread';
import { InstagramConnect } from '../components/InstagramConnect';
import { TemplateEditor } from '../components/TemplateEditor';
import { PlanSelector } from '../components/PlanSelector';
import { useAPI } from '../hooks/useAPI';
import { api } from '../services/api';
import type { DMConversation, InstagramAccount, DMTemplate } from '../types';

export function DashboardPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'conversations' | 'templates' | 'settings' | 'pricing'>('overview');
  const [selectedConversation, setSelectedConversation] = useState<DMConversation | null>(null);
  const [selectedAccount] = useState<string | undefined>();
  const [editingTemplate, setEditingTemplate] = useState<DMTemplate | null>(null);

  // Only load accounts when on settings tab or templates tab
  const accounts = useAPI<InstagramAccount[]>(
    () => api.getInstagramAccounts(),
    [activeTab],
    'instagram-accounts'
  );

  // Only load templates when on templates tab
  const templates = useAPI<DMTemplate[]>(
    () => api.getTemplates(selectedAccount),
    [activeTab, selectedAccount],
    'templates'
  );

  const handleConversationSelect = (conversation: DMConversation) => {
    setSelectedConversation(conversation);
  };

  const handleMessageSent = () => {
    // Refresh conversations if needed
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-lg">
        <div className="p-4 border-b">
          <h1 className="text-xl font-bold">Chatutu</h1>
        </div>
        <nav className="mt-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
              activeTab === 'overview' ? 'bg-blue-50 text-blue-600' : ''
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('conversations')}
            className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
              activeTab === 'conversations' ? 'bg-blue-50 text-blue-600' : ''
            }`}
          >
            Conversations
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
              activeTab === 'templates' ? 'bg-blue-50 text-blue-600' : ''
            }`}
          >
            Templates
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
              activeTab === 'settings' ? 'bg-blue-50 text-blue-600' : ''
            }`}
          >
            Settings
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
              activeTab === 'pricing' ? 'bg-blue-50 text-blue-600' : ''
            }`}
          >
            Pricing
          </button>
        </nav>
      </div>

      {/* Main content */}
      <div className="ml-64">
        {activeTab === 'overview' && <Dashboard />}

        {activeTab === 'conversations' && (
          <div className="flex h-screen">
            <div className="w-1/3">
              <ConversationList
                accountId={selectedAccount}
                onSelectConversation={handleConversationSelect}
              />
            </div>
            <div className="flex-1">
              {selectedConversation ? (
                <MessageThread
                  conversation={selectedConversation}
                  onMessageSent={handleMessageSent}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Select a conversation to view messages
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="p-8">
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold">Templates</h2>
              <button
                onClick={() => setEditingTemplate(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                New Template
              </button>
            </div>
            {editingTemplate !== undefined ? (
              <div className="bg-white p-6 rounded-lg shadow">
                <TemplateEditor
                  templateId={editingTemplate?.id}
                  accountId={selectedAccount}
                  onSave={() => {
                    setEditingTemplate(null);
                    templates.refetch();
                  }}
                  onCancel={() => setEditingTemplate(null)}
                />
              </div>
            ) : (
              <div className="space-y-4">
                {templates.data?.map((template) => (
                  <div key={template.id} className="bg-white p-4 rounded-lg shadow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{template.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{template.template_content}</p>
                      </div>
                      <button
                        onClick={() => setEditingTemplate(template)}
                        className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="p-8">
            <h2 className="text-2xl font-bold mb-6">Settings</h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Instagram Accounts</h3>
                {accounts.loading ? (
                  <p>Loading accounts...</p>
                ) : accounts.data && accounts.data.length > 0 ? (
                  <div className="space-y-2">
                    {accounts.data.map((account) => (
                      <div key={account.id} className="flex justify-between items-center p-3 border rounded">
                        <div>
                          <p className="font-medium">{account.business_account_name}</p>
                          <p className="text-sm text-gray-500">
                            Connected {new Date(account.connected_at).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={async () => {
                            await api.disconnectInstagramAccount(account.id);
                            accounts.refetch();
                          }}
                          className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Disconnect
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <InstagramConnect />
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pricing' && (
          <div className="p-8">
            <h2 className="text-2xl font-bold mb-6">Pricing Plans</h2>
            <PlanSelector />
          </div>
        )}
      </div>
    </div>
  );
}
